import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { closeBudget } from 'loot-core/src/client/actions';
import { ChevronsUpDown, DollarSign } from 'lucide-react';

import { useMetadataPref } from '../../hooks/useMetadataPref';
import { useNavigate } from '../../hooks/useNavigate';
import { useDispatch } from '../../redux';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '../ui/sidebar';

export function BudgetSwitcher() {
  const { t } = useTranslation();
  const [budgetName, setBudgetNamePref] = useMetadataPref('budgetName');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [renaming, setRenaming] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);

  function onMenuSelect(type: string) {
    switch (type) {
      case 'rename':
        setRenaming(true);
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'close':
        dispatch(closeBudget());
        break;
      default:
    }
  }

  function onRenameConfirm() {
    const newName = renameInputRef.current?.value?.trim();
    if (newName) {
      setBudgetNamePref(newName);
    }
    setRenaming(false);
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                onContextMenu={e => { e.preventDefault(); setMenuOpen(true); }}
              >
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <DollarSign className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {budgetName || t('Unnamed')}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-56 rounded-lg"
              align="start"
              side="bottom"
              sideOffset={4}
            >
              <DropdownMenuItem onSelect={() => onMenuSelect('rename')}>
                {t('Rename budget')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onMenuSelect('settings')}>
                {t('Settings')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => onMenuSelect('close')}
                className="text-destructive focus:text-destructive"
              >
                {t('Close file')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      <Dialog open={renaming} onOpenChange={open => !open && setRenaming(false)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('Rename budget')}</DialogTitle>
          </DialogHeader>
          <Input
            ref={renameInputRef}
            defaultValue={budgetName ?? ''}
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter') onRenameConfirm();
              if (e.key === 'Escape') setRenaming(false);
            }}
          />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">{t('Cancel')}</Button>
            </DialogClose>
            <Button onClick={onRenameConfirm}>{t('Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
